import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import AttendanceGate from '../components/AttendanceGate';
import { useAttendanceGate } from '../hooks/useAttendanceGate';
import GeoLocationCapture from '../components/GeoLocationCapture';
import SearchableSelect from '../components/SearchableSelect';
// OUTLET (EDIT) sheet: per-segment volumes, shared with visit-time capture.
import { SEGMENT_UOM, SEGMENTS, ACTIVATION_TYPES } from '../lib/outletSegments';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Store, 
  Plus, 
  Search, 
  Loader2,
  Phone,
  Edit,
  Trash2,
  X,
  MapPin,
  User,
  Building
} from 'lucide-react';
import { toast } from 'sonner';
import { useWindowedList } from '../hooks/useWindowedList';
import { cn } from '../lib/utils';



// KEYNOTE: the edit option lives only in the window before approval. Once an
// admin has approved (or rejected) the record it is settled, and only an admin
// can still change it.
const isEditable = (record, isAdmin) =>
  isAdmin || !['approved', 'rejected'].includes(record?.approval_status);


const Outlets = () => {
  const { api, canDelete, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOutlets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOutlets = async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data);
    } catch (error) {
      console.error('Failed to fetch outlets:', error);
      toast.error('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this outlet?')) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success('Outlet deleted');
      fetchOutlets();
    } catch (error) {
      toast.error('Failed to delete outlet');
    }
  };

  const filteredOutlets = outlets.filter(outlet => {
    return outlet.outlet_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      outlet.outlet_id?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Only a screenful is kept in the DOM; the rest is a tap away.
  const windowed = useWindowedList(filteredOutlets);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="outlets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Outlets</h1>
            <p className="text-sm text-slate-500">{outlets.length} outlets registered</p>
          </div>
          <Button
            onClick={() => navigate('/outlets/new')}
            className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white rounded-xl h-11 shadow-lg shadow-sky-200"
            data-testid="add-outlet-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Outlet
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            placeholder="Search outlets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-xl border-slate-200"
            data-testid="search-outlets"
          />
        </div>

        {/* Outlets List */}
        <div className="space-y-3">
          {filteredOutlets.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-2xl">
              <Store className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No outlets found</p>
            </div>
          ) : (
            windowed.shown.map((outlet) => (
              <div
                key={outlet.id}
                className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-all card-hover"
                data-testid={`outlet-card-${outlet.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Store className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                        {outlet.outlet_name}
                        {outlet.approval_status && outlet.approval_status !== 'approved' && (
                          <span className={cn(
                            'px-1.5 py-0.5 rounded text-[10px] font-medium',
                            outlet.approval_status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          )}>
                            {outlet.approval_status.toUpperCase()}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-500">{outlet.outlet_id}</p>
                      {outlet.contacts?.[0]?.phone && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {outlet.contacts[0].phone}
                        </p>
                      )}
                      {outlet.geo_location && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          Location captured
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditable(outlet, isAdmin) ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/outlets/${outlet.id}/edit`)}
                        className="h-8 w-8"
                        data-testid={`edit-outlet-${outlet.id}`}
                      >
                        <Edit className="w-4 h-4 text-slate-500" />
                      </Button>
                    ) : (
                      <span
                        className="text-[10px] font-medium text-slate-400 px-2 py-1 rounded bg-slate-50 whitespace-nowrap"
                        title="Approved outlets can no longer be edited"
                        data-testid={`edit-locked-outlet-${outlet.id}`}
                      >
                        Locked
                      </span>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(outlet.id)}
                        className="h-8 w-8"
                        data-testid={`delete-outlet-${outlet.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

        {windowed.hasMore && (
          <button
            type="button"
            onClick={windowed.showMore}
            className="w-full bg-white rounded-2xl border border-slate-200 p-3 text-sm font-medium text-sky-700 hover:bg-slate-50"
            data-testid="show-more"
          >
            Show more outlets · {windowed.shown.length} of {windowed.total}
          </button>
        )}
        </div>
      </div>
    </Layout>
  );
};

// New Outlet Form (with Distributor link and auto-fill)
export const OutletForm = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  // KEYNOTE 15/16: a new outlet can only be added while the day is open.
  const gate = useAttendanceGate();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditMode);
  const [location, setLocation] = useState(null);
  const [distributorSearch, setDistributorSearch] = useState('');
  const [showDistributorDropdown, setShowDistributorDropdown] = useState(false);
  const [masters, setMasters] = useState({
    distributors: [],  // customers/distributors the outlet is tagged under
    retailerTypes: [],
    assets: [],
    cities: [],
    purposes: []
  });
  const [formData, setFormData] = useState({
    outlet_name: '',
    distributor_id: '',
    route: '',
    zone: '',
    retailer_type_id: '',
    city: '',
    asset_id: '',
    activation_type: '',
    purpose: '',
    pincode: '',
    address: ''
  });
  const [contacts, setContacts] = useState([{ name: '', phone: '', is_primary: true }]);
  const [volumes, setVolumes] = useState(() =>
    Object.fromEntries(SEGMENTS.map((s) => [s, '']))
  );
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  const cityOptions = useMemo(
    () => (masters.cities || []).map((c) => ({ value: c.name, label: c.name })),
    [masters.cities]
  );

  useEffect(() => {
    fetchMasters();
    if (isEditMode) {
      fetchOutlet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOutlet = async () => {
    try {
      const response = await api.get(`/outlets/${id}`);
      const outlet = response.data;
      setFormData({
        outlet_name: outlet.outlet_name || '',
        distributor_id: outlet.distributor_id || '',
        route: outlet.route || '',
        zone: outlet.zone || '',
        retailer_type_id: outlet.retailer_type_id || '',
        city: outlet.city || '',
        asset_id: outlet.asset_id || '',
        activation_type: outlet.activation_type || '',
        purpose: outlet.purpose || '',
        pincode: outlet.pincode || '',
        address: outlet.address || ''
      });
      if (outlet.contacts && outlet.contacts.length > 0) {
        setContacts(outlet.contacts);
      }
      if (outlet.segment_volumes?.length) {
        setVolumes((prev) => ({
          ...prev,
          ...Object.fromEntries(
            outlet.segment_volumes
              .filter((sv) => SEGMENTS.includes(sv.segment))
              .map((sv) => [sv.segment, String(sv.volume ?? '')])
          )
        }));
      }
      if (outlet.geo_location) {
        setLocation(outlet.geo_location);
      }
      // Fetch distributor details
      if (outlet.distributor_id) {
        try {
          const distResp = await api.get(`/distributors/${outlet.distributor_id}`);
          setSelectedDistributor(distResp.data);
        } catch (e) {
          console.error('Failed to fetch distributor:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch outlet:', error);
      toast.error('Failed to load outlet');
      navigate('/outlets');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchMasters = async () => {
    try {
      const [distributorsRes, retailerTypesRes, assetsRes, citiesRes, purposesRes] = await Promise.all([
        api.get('/distributors'),
        api.get('/masters/retailer-types'),
        api.get('/masters/assets'),
        api.get('/masters/cities'),
        api.get('/masters/outlet-purposes')
      ]);
      setMasters({
        distributors: distributorsRes.data,
        retailerTypes: retailerTypesRes.data,
        assets: assetsRes.data,
        cities: citiesRes.data,
        purposes: purposesRes.data
      });
    } catch (error) {
      console.error('Failed to fetch masters:', error);
      toast.error('Failed to load form data');
    }
  };

  const handleDistributorSelect = (distributor) => {
    setSelectedDistributor(distributor);
    // Tag the outlet under this customer/distributor and pre-fill route/zone from it;
    // the user can still correct either before saving.
    setFormData(prev => ({
      ...prev,
      distributor_id: distributor.id,
      route: prev.route || distributor.route || '',
      zone: prev.zone || distributor.zone || ''
    }));
    setDistributorSearch('');
    setShowDistributorDropdown(false);
  };

  const checkDuplicate = async (phone) => {
    if (!phone || !formData.distributor_id) return false;
    try {
      const response = await api.get(`/outlets/check-duplicate?phone=${phone}&distributor_id=${formData.distributor_id}`);
      if (response.data.exists && (!isEditMode || response.data.outlet?.id !== id)) {
        toast.error(`Outlet with phone ${phone} already exists for this distributor`);
        return true;
      }
    } catch (error) {
      console.error('Failed to check duplicate:', error);
    }
    return false;
  };

  const addContact = () => {
    setContacts([...contacts, { name: '', phone: '', is_primary: false }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    updated[index][field] = value;
    setContacts(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = {
      outlet_name: 'Outlet name',
      distributor_id: 'Customer / Distributor',
      route: 'Route',
      zone: 'Zone',
      retailer_type_id: 'Retailer type',
      city: 'City',
      asset_id: 'Asset in outlet',
      activation_type: 'Activation type'
    };
    const missing = Object.entries(required)
      .filter(([field]) => !String(formData[field] || '').trim())
      .map(([, label]) => label);
    if (missing.length) {
      toast.error(`Required: ${missing.join(', ')}`);
      return;
    }
    if (!location) {
      toast.error('Capture the outlet location — the address is geo-tagged');
      return;
    }

    // Every segment has to be answered for. Zero is a fine answer; blank is not,
    // because the volume report cannot tell "none" from "never asked".
    const blankSegments = SEGMENTS.filter((seg) => String(volumes[seg] ?? '').trim() === '');
    if (blankSegments.length) {
      toast.error(`Enter a volume for: ${blankSegments.join(', ')} (use 0 if not stocked)`);
      return;
    }

    // Check for duplicate phone
    const primaryContact = contacts.find(c => c.phone);
    if (primaryContact && await checkDuplicate(primaryContact.phone)) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        // All six go up, zeros included - dropping them would make a declared
        // nought indistinguishable from a segment nobody filled in.
        segment_volumes: SEGMENTS
          .map((s) => ({ segment: s, volume: parseFloat(volumes[s]) || 0, uom: SEGMENT_UOM[s] })),
        geo_location: { lat: location.lat, lng: location.lng },
        contacts: contacts.filter(c => c.name || c.phone)
      };

      if (isEditMode) {
        await api.put(`/outlets/${id}`, payload);
        toast.success('Outlet updated successfully!');
      } else {
        await api.post('/outlets', payload);
        toast.success('Outlet added — pending admin approval');
      }
      navigate('/outlets');
    } catch (error) {
      console.error('Failed to save outlet:', error);
      const errorMsg = error.response?.data?.detail || (isEditMode ? 'Failed to update outlet' : 'Failed to create outlet');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Filter distributors based on search
  const filteredDistributors = masters.distributors.filter(d => 
    distributorSearch && (
      d.name?.toLowerCase().includes(distributorSearch.toLowerCase()) ||
      d.code?.toLowerCase().includes(distributorSearch.toLowerCase())
    )
  ).slice(0, 10);

  if (fetchingData || (!isEditMode && gate.loading)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  if (!isEditMode && !gate.allowed) {
    return (
      <Layout>
        <div className="py-10">
          <AttendanceGate reason={gate.reason} punchOutTime={gate.punchOutTime} action="a new outlet" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="outlet-form-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isEditMode ? 'Edit Outlet' : 'Add Outlet'}</h1>
          <p className="text-sm text-slate-500">{isEditMode ? 'Update outlet details' : 'Register a new retail outlet'}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* GPS Location (Mandatory - Jio Fencing) */}
          <GeoLocationCapture 
            onLocationCapture={setLocation} 
            required 
            initialLocation={location}
          />

          {/* Distributor Selection with Search */}
          <div className="space-y-2 relative">
            <Label>Distributor/Customer *</Label>
            {selectedDistributor ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-900">{selectedDistributor.name}</p>
                    <p className="text-sm text-emerald-600">{selectedDistributor.code} • Route {selectedDistributor.route} • {selectedDistributor.zone}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedDistributor(null);
                    setFormData(prev => ({ ...prev, distributor_id: '' }));
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search distributor by name or code..."
                  value={distributorSearch}
                  onChange={(e) => {
                    setDistributorSearch(e.target.value);
                    setShowDistributorDropdown(true);
                  }}
                  onFocus={() => setShowDistributorDropdown(true)}
                  className="pl-10 h-12 rounded-xl"
                  data-testid="distributor-search"
                />
                {showDistributorDropdown && filteredDistributors.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {filteredDistributors.map(distributor => (
                      <button
                        key={distributor.id}
                        type="button"
                        onClick={() => handleDistributorSelect(distributor)}
                        className="w-full p-3 text-left hover:bg-slate-50 border-b last:border-b-0 flex items-center gap-3"
                      >
                        <Building className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="font-medium text-slate-900">{distributor.name}</p>
                          <p className="text-xs text-slate-500">{distributor.code} • Route {distributor.route} • {distributor.zone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-slate-500">Select the distributor this outlet belongs to</p>
          </div>

          {/* Outlet Name (entered manually) */}
          <div className="space-y-2">
            <Label>Outlet Name *</Label>
            <Input
              value={formData.outlet_name}
              onChange={(e) => setFormData({ ...formData, outlet_name: e.target.value })}
              placeholder="Enter outlet name"
              className="h-12 rounded-xl"
              required
              data-testid="outlet-name"
            />
          </div>

          {/* Route & Zone - entered by the user, one time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Route *</Label>
              <Input
                value={formData.route}
                onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                placeholder="Analytical route"
                className="h-12 rounded-xl"
                data-testid="outlet-route"
              />
            </div>
            <div className="space-y-2">
              <Label>Zone *</Label>
              <Input
                value={formData.zone}
                onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                placeholder="Analytical zone"
                className="h-12 rounded-xl"
                data-testid="outlet-zone"
              />
            </div>
          </div>

          {/* Retailer Type - admin-managed dropdown */}
          <div className="space-y-2">
            <Label>Retailer Type *</Label>
            <Select
              value={formData.retailer_type_id}
              onValueChange={(value) => setFormData({ ...formData, retailer_type_id: value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="retailer-type">
                <SelectValue placeholder="Select retailer type" />
              </SelectTrigger>
              <SelectContent>
                {masters.retailerTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* City - admin-managed master, several hundred entries, so type to search */}
          <div className="space-y-2">
            <Label>City *</Label>
            <SearchableSelect
              testId="city"
              value={formData.city}
              onChange={(value) => setFormData({ ...formData, city: value })}
              options={cityOptions}
              placeholder="Select city"
              searchPlaceholder="Type a city name…"
              emptyMessage="No city matches that"
            />
          </div>

          {/* Activation Type (renamed from "Type") */}
          <div className="space-y-2">
            <Label>Activation Type</Label>
            <Select
              value={formData.activation_type || "none"}
              onValueChange={(value) => setFormData({ ...formData, activation_type: value === "none" ? "" : value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="activation-type">
                <SelectValue placeholder="Select activation type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select Type</SelectItem>
                {ACTIVATION_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Asset in outlet - admin-managed dropdown */}
          <div className="space-y-2">
            <Label>Asset in Outlet *</Label>
            <Select
              value={formData.asset_id}
              onValueChange={(value) => setFormData({ ...formData, asset_id: value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="asset">
                <SelectValue placeholder="Select asset" />
              </SelectTrigger>
              <SelectContent>
                {masters.assets.map((asset) => (
                  <SelectItem key={asset.id} value={asset.id}>{asset.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Purpose - admin-managed dropdown (was "Remarks") */}
          <div className="space-y-2">
            <Label>Purpose</Label>
            <Select
              value={formData.purpose || "none"}
              onValueChange={(value) => setFormData({ ...formData, purpose: value === "none" ? "" : value })}
            >
              <SelectTrigger className="h-12 rounded-xl" data-testid="outlet-purpose">
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select Purpose</SelectItem>
                {masters.purposes.map((p) => (
                  <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Volume by segment */}
          <div className="space-y-3">
            <div>
              <Label>Volume</Label>
              <p className="text-xs text-slate-500 mt-1">Monthly volume per segment. Leave blank where not stocked.</p>
            </div>
            <div className="bg-slate-50 rounded-xl divide-y divide-slate-200 overflow-hidden">
              <div className="grid grid-cols-[1fr_7rem] gap-2 px-3 py-2 bg-slate-100">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Segment</span>
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Quantity</span>
              </div>
              {SEGMENTS.map((segment) => (
                <div key={segment} className="grid grid-cols-[1fr_7rem] gap-2 items-center px-3 py-2">
                  <span className="text-sm text-slate-700">
                    {segment}
                    <span className="text-slate-400"> (in {SEGMENT_UOM[segment]})</span>
                  </span>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={volumes[segment]}
                    onChange={(e) => setVolumes({ ...volumes, [segment]: e.target.value })}
                    placeholder="0"
                    className="h-9 rounded-lg bg-white"
                    data-testid={`volume-${segment.toLowerCase().replace(' ', '-')}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Optional details */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <p className="text-sm font-medium text-slate-500 mb-4">Optional Information</p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Pin Code</Label>
                <Input
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  placeholder="Pin code"
                  className="h-10 rounded-xl"
                  data-testid="pincode"
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                  className="h-10 rounded-xl"
                  data-testid="address"
                />
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Contacts</Label>
              <Button type="button" variant="outline" size="sm" onClick={addContact} className="rounded-xl">
                <Plus className="w-4 h-4 mr-1" /> Add Contact
              </Button>
            </div>
            {contacts.map((contact, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input
                  placeholder="Name"
                  value={contact.name}
                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                  className="flex-1 h-10 rounded-xl"
                />
                <Input
                  placeholder="Phone *"
                  value={contact.phone}
                  onChange={(e) => updateContact(index, 'phone', e.target.value)}
                  className="flex-1 h-10 rounded-xl"
                  onBlur={() => contact.phone && checkDuplicate(contact.phone)}
                />
                {contacts.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(index)}>
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
            <p className="text-xs text-amber-600">* Phone number is used to detect duplicate entries</p>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/outlets')}
              className="flex-1 h-12 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
              data-testid="submit-outlet"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Outlet' : 'Create Outlet'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Outlets;
